import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
} from "@material-tailwind/react";
import {
  AcademicCapIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import {
  ADMIN_SECTIONS,
  adminPathFromSection,
  adminSectionFromParam,
  adminSectionFromPathname,
} from "../constants/adminSections";
import PortalSelect from "../components/PortalSelect";
import AdminFilterToolbar, {
  AdminFilterField,
} from "../components/admin/AdminFilterToolbar";
import { formatMexicoLocation, getStates, getStateByCode } from "../utils/mexicoLocations";
import {
  labelAppointmentModality,
  labelConsultationModesSetting,
} from "../utils/consultationModes";
import AdminClientsPanel from "../components/admin/AdminClientsPanel";
import AdminPsychDetailDrawer from "../components/admin/AdminPsychDetailDrawer";
import AdminApptDetailDrawer from "../components/admin/AdminApptDetailDrawer";
import AdminRevistasPanel from "../components/admin/AdminRevistasPanel";

/** Shared pagination footer — same look as AdminClientsPanel */
function PaginationFooter({
  page,
  pageCount,
  pageSize,
  total,
  setPage,
  setPageSize,
  itemLabel = "elemento",
}) {
  const rangeFrom = total === 0 ? 0 : page * pageSize + 1;
  const rangeTo = Math.min((page + 1) * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-lavender/15 px-4 py-3 bg-white">
      <div className="flex items-center gap-2 portal-meta">
        <span className="portal-label">Por página</span>
        <PortalSelect
          size="sm"
          className="!w-auto min-w-[4.5rem]"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {[10, 25, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </PortalSelect>
      </div>
      <div className="flex items-center gap-3 portal-meta tabular-nums">
        <span>
          Mostrando {rangeFrom}–{rangeTo} de {total}{" "}
          {itemLabel}
          {total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 text-brand-navy disabled:opacity-35 hover:bg-brand-lilac/30"
            aria-label="Página anterior"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 text-brand-navy disabled:opacity-35 hover:bg-brand-lilac/30"
            aria-label="Página siguiente"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 500;

function shortConsultationMode(cm) {
  if (cm === "virtual") return "Virtual";
  if (cm === "in_person") return "Presencial";
  if (cm === "both") return "Ambas";
  return labelConsultationModesSetting(cm);
}

function displayCell(value) {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  return text || "—";
}

function formatApptDate(iso) {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  const date = d.toLocaleDateString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("es-MX", {
    timeZone: "America/Mexico_City",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return { date, time };
}

function formatApptTimeRange(startUtc, endUtc) {
  const start = formatApptDate(startUtc);
  if (!endUtc) return { date: start.date, time: start.time };
  const endTime = new Date(endUtc).toLocaleTimeString("es-MX", {
    timeZone: "America/Mexico_City",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return { date: start.date, time: `${start.time} – ${endTime}` };
}

function labelApptStatus(status) {
  const labels = {
    scheduled: "Programada",
    cancelled: "Cancelada",
  };
  return labels[status] || displayCell(status);
}

function ymdToStartUtc(ymd) {
  if (!ymd) return "";
  return `${ymd}T00:00:00Z`;
}

function ymdToEndUtc(ymd) {
  if (!ymd) return "";
  return `${ymd}T23:59:59Z`;
}

const SEARCH_PLACEHOLDERS = {
  clients: "Buscar por nombre, correo, teléfono, estado o empresa…",
  psychologists: "Buscar por nombre, correo, especialidad o ubicación…",
  appointments: "Buscar por cliente, psicólogo o estado…",
};

const psychCellClass = "portal-table-cell px-3 py-2.5 align-middle overflow-hidden";
const dateInputCls =
  "w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2.5 text-[1rem] text-brand-dark focus:border-brand-lavender focus:outline-none focus:ring-1 focus:ring-brand-lavender/40 disabled:opacity-50";

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function PortalAdminHome() {
  const { user, isAuthenticated } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const legacySeccion = new URLSearchParams(location.search).get("seccion");
  const activeSection = adminSectionFromPathname(location.pathname);

  const [clients, setClients] = useState([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [psychs, setPsychs] = useState([]);
  const [appts, setAppts] = useState([]);

  const [psychPage, setPsychPage] = useState(0);
  const [psychPageSize, setPsychPageSize] = useState(10);
  const [apptPage, setApptPage] = useState(0);
  const [apptPageSize, setApptPageSize] = useState(10);

  const [loadingSection, setLoadingSection] = useState(false);
  const [sectionError, setSectionError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 350);

  const [psychModality, setPsychModality] = useState("");
  const [psychState, setPsychState] = useState("");
  const [apptStatus, setApptStatus] = useState("");
  const [apptModality, setApptModality] = useState("");
  const [apptPsychId, setApptPsychId] = useState("");
  const [apptFromYmd, setApptFromYmd] = useState("");
  const [apptToYmd, setApptToYmd] = useState("");
  const [apptShowAll, setApptShowAll] = useState(false);

  const [psychOptions, setPsychOptions] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedPsych, setSelectedPsych] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [cancelApptTarget, setCancelApptTarget] = useState(null);
  const [cancellingAppt, setCancellingAppt] = useState(false);

  const fetchGen = useRef(0);
  const psychOptionsLoaded = useRef(false);

  const activeSectionLabel =
    ADMIN_SECTIONS.find((s) => s.key === activeSection)?.label || "Clientes";

  // Compat: /admin?seccion=clientes → /admin/clientes
  useEffect(() => {
    if (!legacySeccion) return;
    const key = adminSectionFromParam(legacySeccion);
    navigate(adminPathFromSection(key), { replace: true });
  }, [legacySeccion, navigate]);

  useEffect(() => {
    setSearchQuery("");
    setPsychModality("");
    setPsychState("");
    setApptStatus("");
    setApptModality("");
    setApptPsychId("");
    setApptFromYmd("");
    setApptToYmd("");
    setApptShowAll(false);
  }, [activeSection]);

  const loadClients = useCallback(async (q) => {
    const r = await portalApi.adminClients({ q: q || undefined, limit: 500, offset: 0 });
    setClients(r.clients || []);
    setClientsTotal(r.total ?? (r.clients || []).length);
  }, []);

  const loadPsychs = useCallback(async (q, modality, stateCode) => {
    const r = await portalApi.adminPsychologists({
      q: q || undefined,
      modality: modality || undefined,
      state: stateCode || undefined,
      limit: 500,
      offset: 0,
    });
    setPsychs(r.psychologists || []);
  }, []);

  const loadAppts = useCallback(
    async ({ q, status, modality, psychologistId, fromYmd, toYmd, showAll }) => {
      const params = {
        limit: PAGE_SIZE,
        offset: 0,
        q: q || undefined,
        status: status || undefined,
        modality: modality || undefined,
        psychologistId: psychologistId || undefined,
      };
      if (showAll) {
        params.from = "all";
      } else if (fromYmd) {
        params.from = ymdToStartUtc(fromYmd);
      }
      if (toYmd) {
        params.to = ymdToEndUtc(toYmd);
      }
      const r = await portalApi.adminAppointments(params);
      const rows = r.appointments || [];
      setAppts(rows);
    },
    []
  );

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "admin") return;
    if (activeSection === "revistas") {
      setLoadingSection(false);
      setSectionError("");
      return;
    }
    const gen = ++fetchGen.current;
    setSectionError("");
    setLoadingSection(true);

    (async () => {
      try {
        if (activeSection === "clients") {
          await loadClients(debouncedSearch);
        } else if (activeSection === "psychologists") {
          await loadPsychs(debouncedSearch, psychModality, psychState);
        } else if (activeSection === "appointments") {
          if (!psychOptionsLoaded.current) {
            try {
              const pr = await portalApi.adminPsychologists({ limit: 500 });
              if (gen === fetchGen.current) {
                setPsychOptions(pr.psychologists || []);
                psychOptionsLoaded.current = true;
              }
            } catch {
              /* filtro psych opcional */
            }
          }
          await loadAppts({
            q: debouncedSearch,
            status: apptStatus,
            modality: apptModality,
            psychologistId: apptPsychId,
            fromYmd: apptFromYmd,
            toYmd: apptToYmd,
            showAll: apptShowAll,
          });
        }
      } catch (e) {
        if (gen === fetchGen.current) {
          setSectionError(e.message || "No se pudieron cargar los datos");
          toast.error(e.message || "No se pudieron cargar los datos");
        }
      } finally {
        if (gen === fetchGen.current) setLoadingSection(false);
      }
    })();
  }, [
    isAuthenticated,
    user,
    activeSection,
    debouncedSearch,
    psychModality,
    psychState,
    apptStatus,
    apptModality,
    apptPsychId,
    apptFromYmd,
    apptToYmd,
    apptShowAll,
    loadClients,
    loadPsychs,
    loadAppts,
  ]);

  // Reset pages when filters/search change
  useEffect(() => { setPsychPage(0); }, [debouncedSearch, psychModality, psychState]);
  useEffect(() => { setApptPage(0); }, [debouncedSearch, apptStatus, apptModality, apptPsychId, apptFromYmd, apptToYmd, apptShowAll]);

  // Paginated rows
  const psychPageCount = Math.max(1, Math.ceil(psychs.length / psychPageSize));
  const psychSafePage = Math.min(psychPage, psychPageCount - 1);
  const psychStart = psychSafePage * psychPageSize;
  const psychRows = psychs.slice(psychStart, psychStart + psychPageSize);

  const apptPageCount = Math.max(1, Math.ceil(appts.length / apptPageSize));
  const apptSafePage = Math.min(apptPage, apptPageCount - 1);
  const apptStart = apptSafePage * apptPageSize;
  const apptRows = appts.slice(apptStart, apptStart + apptPageSize);

  const hasActiveFilters = useMemo(() => {
    if (activeSection === "psychologists") {
      return Boolean(psychModality || psychState);
    }
    if (activeSection === "appointments") {
      return Boolean(
        apptStatus ||
          apptModality ||
          apptPsychId ||
          apptFromYmd ||
          apptToYmd ||
          apptShowAll
      );
    }
    return false;
  }, [
    activeSection,
    psychModality,
    psychState,
    apptStatus,
    apptModality,
    apptPsychId,
    apptFromYmd,
    apptToYmd,
    apptShowAll,
  ]);

  function clearFilters() {
    setPsychModality("");
    setPsychState("");
    setApptStatus("");
    setApptModality("");
    setApptPsychId("");
    setApptFromYmd("");
    setApptToYmd("");
    setApptShowAll(false);
  }

  const psychFilterCount = [psychModality, psychState].filter(Boolean).length;
  const apptFilterCount = [
    apptStatus,
    apptModality,
    apptPsychId,
    apptFromYmd,
    apptToYmd,
    apptShowAll,
  ].filter(Boolean).length;

  const mexicoStates = useMemo(() => getStates(), []);

  const psychChips = useMemo(() => {
    const list = [];
    if (psychModality) {
      const short = {
        virtual: "Virtual",
        in_person: "Presencial",
        both: "Ambas",
      };
      list.push({
        id: "psych-modality",
        field: "Modalidad",
        value: short[psychModality] || psychModality,
        onRemove: () => setPsychModality(""),
      });
    }
    if (psychState) {
      list.push({
        id: "psych-state",
        field: "Estado",
        value: getStateByCode(psychState)?.name || psychState,
        onRemove: () => setPsychState(""),
      });
    }
    return list;
  }, [psychModality, psychState]);

  const apptChips = useMemo(() => {
    const list = [];
    if (apptStatus) {
      list.push({
        id: "appt-status",
        field: "Estado",
        value: labelApptStatus(apptStatus),
        onRemove: () => setApptStatus(""),
      });
    }
    if (apptModality) {
      list.push({
        id: "appt-modality",
        field: "Modalidad",
        value: labelAppointmentModality(apptModality),
        onRemove: () => setApptModality(""),
      });
    }
    if (apptPsychId) {
      const name =
        psychOptions.find((p) => p.psychologistId === apptPsychId)?.name ||
        apptPsychId;
      list.push({
        id: "appt-psych",
        field: "Psicólogo",
        value: name,
        onRemove: () => setApptPsychId(""),
      });
    }
    if (apptFromYmd) {
      list.push({
        id: "appt-from",
        field: "Desde",
        value: apptFromYmd,
        onRemove: () => setApptFromYmd(""),
      });
    }
    if (apptToYmd) {
      list.push({
        id: "appt-to",
        field: "Hasta",
        value: apptToYmd,
        onRemove: () => setApptToYmd(""),
      });
    }
    if (apptShowAll) {
      list.push({
        id: "appt-all",
        field: "Historial",
        value: "Completo",
        onRemove: () => setApptShowAll(false),
      });
    }
    return list;
  }, [
    apptStatus,
    apptModality,
    apptPsychId,
    apptFromYmd,
    apptToYmd,
    apptShowAll,
    psychOptions,
  ]);

  function askDeletePsych(p) {
    setDeleteTarget(p);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
  }

  async function confirmDeletePsych() {
    if (!deleteTarget?.psychologistId) return;
    setDeleting(true);
    try {
      await portalApi.adminDeletePsychologist(deleteTarget.psychologistId);
      setPsychs((prev) =>
        prev.filter((x) => x.psychologistId !== deleteTarget.psychologistId)
      );
      setPsychOptions((prev) =>
        prev.filter((x) => x.psychologistId !== deleteTarget.psychologistId)
      );
      toast.success("Psicólogo eliminado");
      setDeleteTarget(null);
      setSelectedPsych((cur) =>
        cur?.psychologistId === deleteTarget.psychologistId ? null : cur
      );

    } catch (e) {
      toast.error(e.message || "No se pudo eliminar");
    } finally {
      setDeleting(false);
    }
  }

  function askCancelAppt(appt) {
    setCancelApptTarget(appt);
  }

  function closeCancelApptDialog() {
    if (cancellingAppt) return;
    setCancelApptTarget(null);
  }

  async function confirmCancelAppt() {
    if (!cancelApptTarget?.appointmentId) return;
    setCancellingAppt(true);
    try {
      await portalApi.cancelAppointment(cancelApptTarget.appointmentId);
      setAppts((prev) =>
        prev.map((a) =>
          a.appointmentId === cancelApptTarget.appointmentId
            ? { ...a, status: "cancelled" }
            : a
        )
      );
      // Actualiza el drawer si la cita cancelada estaba abierta
      setSelectedAppt((cur) =>
        cur?.appointmentId === cancelApptTarget.appointmentId
          ? { ...cur, status: "cancelled" }
          : cur
      );
      toast.success("Cita cancelada");
      setCancelApptTarget(null);
    } catch (e) {
      toast.error(e.message || "No se pudo cancelar la cita");
    } finally {
      setCancellingAppt(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "admin") {
    if (user?.role === "psychologist") {
      return <Navigate to="/psicologo" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <PortalShell
      wide
      title={activeSectionLabel}
      subtitle={`Consulta y gestiona ${activeSectionLabel.toLowerCase()} del portal.`}
    >
      <div className="mb-4">
        {activeSection === "psychologists" && (
          <AdminFilterToolbar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={SEARCH_PLACEHOLDERS.psychologists}
            searchAriaLabel="Buscar psicólogos"
            filterCount={psychFilterCount}
            onClearAll={hasActiveFilters ? clearFilters : undefined}
            chips={psychChips}
            actions={
              <Link
                to="/admin/psicologos/nuevo"
                className="inline-flex h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-brand-lavender px-5 text-[1rem] font-semibold text-white shadow-md shadow-brand-lavender/25 hover:brightness-105 transition-all"
              >
                <AcademicCapIcon className="h-5 w-5" aria-hidden />
                Nuevo psicólogo
              </Link>
            }
          >
            <AdminFilterField label="Modalidad">
              <PortalSelect
                className="w-full"
                value={psychModality}
                onChange={(e) => setPsychModality(e.target.value)}
              >
                <option value="">Todas</option>
                <option value="virtual">Virtual</option>
                <option value="in_person">Presencial</option>
                <option value="both">Ambas</option>
              </PortalSelect>
            </AdminFilterField>
            <AdminFilterField label="Estado">
              <PortalSelect
                className="w-full"
                value={psychState}
                onChange={(e) => setPsychState(e.target.value)}
                aria-label="Filtrar por estado"
              >
                <option value="">Todos</option>
                {mexicoStates.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </PortalSelect>
            </AdminFilterField>
          </AdminFilterToolbar>
        )}

        {activeSection === "appointments" && (
          <AdminFilterToolbar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={SEARCH_PLACEHOLDERS.appointments}
            searchAriaLabel="Buscar citas"
            filterCount={apptFilterCount}
            onClearAll={hasActiveFilters ? clearFilters : undefined}
            chips={apptChips}
          >
            <AdminFilterField label="Estado">
              <PortalSelect
                className="w-full"
                value={apptStatus}
                onChange={(e) => setApptStatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="scheduled">Programada</option>
                <option value="cancelled">Cancelada</option>
              </PortalSelect>
            </AdminFilterField>
            <AdminFilterField label="Modalidad">
              <PortalSelect
                className="w-full"
                value={apptModality}
                onChange={(e) => setApptModality(e.target.value)}
              >
                <option value="">Todas</option>
                <option value="virtual">Virtual</option>
                <option value="in_person">Presencial</option>
              </PortalSelect>
            </AdminFilterField>
            <AdminFilterField label="Psicólogo">
              <PortalSelect
                className="w-full"
                value={apptPsychId}
                onChange={(e) => setApptPsychId(e.target.value)}
              >
                <option value="">Todos</option>
                {psychOptions.map((p) => (
                  <option key={p.psychologistId} value={p.psychologistId}>
                    {p.name}
                  </option>
                ))}
              </PortalSelect>
            </AdminFilterField>
            <AdminFilterField label="Desde">
              <input
                type="date"
                className={dateInputCls}
                value={apptFromYmd}
                disabled={apptShowAll}
                onChange={(e) => setApptFromYmd(e.target.value)}
              />
            </AdminFilterField>
            <AdminFilterField label="Hasta">
              <input
                type="date"
                className={dateInputCls}
                value={apptToYmd}
                onChange={(e) => setApptToYmd(e.target.value)}
              />
            </AdminFilterField>
            <label className="inline-flex items-center gap-2 portal-value cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={apptShowAll}
                onChange={(e) => {
                  setApptShowAll(e.target.checked);
                  if (e.target.checked) setApptFromYmd("");
                }}
              />
              Ver historial completo
            </label>
            {!apptShowAll && !apptFromYmd ? (
              <p className="portal-meta">
                Por defecto: últimos 90 días y citas futuras.
              </p>
            ) : null}
          </AdminFilterToolbar>
        )}
      </div>

      {loadingSection && activeSection !== "clients" && activeSection !== "revistas" && (
        <div className="flex items-center gap-3 text-brand-gray py-16 justify-center">
          <Spinner className="h-8 w-8" />
          Cargando {activeSectionLabel.toLowerCase()}…
        </div>
      )}

      {!loadingSection && sectionError && activeSection !== "clients" && activeSection !== "revistas" && (
        <p className="text-center text-red-600 text-sm py-8">{sectionError}</p>
      )}

      {activeSection === "clients" && (
        <>
          {sectionError ? (
            <p className="text-center text-red-600 text-sm py-8">{sectionError}</p>
          ) : (
            <AdminClientsPanel
              clients={clients}
              total={clientsTotal}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              loading={loadingSection}
            />
          )}
        </>
      )}

      {activeSection === "revistas" && <AdminRevistasPanel />}

      {!loadingSection && !sectionError && activeSection === "psychologists" && (
        <section className="mb-10 scroll-mt-24 space-y-4">
          <div className="overflow-hidden rounded-xl border border-brand-lavender/20 bg-white shadow-sm">
            {psychs.length === 0 ? (
              <div className="py-16 px-6 text-center">
                <p className="portal-value font-semibold text-brand-navy mb-1">
                  {debouncedSearch || psychModality || psychState
                    ? "Ningún psicólogo coincide con los filtros."
                    : "Aún no hay psicólogos."}
                </p>
                {!debouncedSearch && !psychModality && !psychState ? (
                  <Link to="/admin/psicologos/nuevo" className="portal-meta text-brand-lavender hover:underline">
                    Crear uno
                  </Link>
                ) : null}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] table-fixed border-collapse">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "19%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "6%" }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-brand-lavender/15 bg-brand-lilac/20">
                      <th className="portal-table-head px-4 py-3 text-left">Nombre</th>
                      <th className="portal-table-head px-4 py-3 text-left">Correo</th>
                      <th className="portal-table-head px-4 py-3 text-left">Teléfono</th>
                      <th className="portal-table-head px-4 py-3 text-left">Ubicación</th>
                      <th className="portal-table-head px-4 py-3 text-left">Dirección</th>
                      <th className="portal-table-head px-4 py-3 text-left">Modalidad</th>
                      <th className="portal-table-head px-4 py-3 text-left">Acceso</th>
                      <th className="portal-table-head px-4 py-3 text-right"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {psychRows.map((p) => {
                      const loc = formatMexicoLocation(p.location);
                      return (
                        <tr
                          key={p.psychologistId}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedPsych(p)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedPsych(p);
                            }
                          }}
                          className="border-b border-brand-lavender/10 last:border-0 hover:bg-brand-lilac/20 transition-colors cursor-pointer"
                        >
                          <td className={psychCellClass}>
                            <span className="block truncate font-semibold text-brand-navy" title={p.name || undefined}>
                              {displayCell(p.name)}
                            </span>
                          </td>
                          <td className={psychCellClass}>
                            {p.email ? (
                              <a
                                href={`mailto:${p.email}`}
                                title={p.email}
                                className="block truncate text-brand-lavender hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.email}
                              </a>
                            ) : "—"}
                          </td>
                          <td className={psychCellClass}>
                            {p.phone ? (
                              <a
                                href={`tel:${String(p.phone).replace(/\s/g, "")}`}
                                title={String(p.phone)}
                                className="block truncate hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.phone}
                              </a>
                            ) : "—"}
                          </td>
                          <td className={psychCellClass}>
                            <span className="block truncate" title={loc !== "—" ? loc : undefined}>
                              {loc}
                            </span>
                          </td>
                          <td className={psychCellClass}>
                            <span className="block truncate" title={p.address || undefined}>
                              {displayCell(p.address)}
                            </span>
                          </td>
                          <td className={psychCellClass}>
                            {shortConsultationMode(p.consultationModes)}
                          </td>
                          <td className={psychCellClass}>
                            {p.hasUserLogin ? "Sí" : "No"}
                          </td>
                          <td className={`${psychCellClass} text-right`}>
                            <div
                              className="inline-flex items-center justify-end gap-1"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/psicologos/${p.psychologistId}/horario`)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-brand-lavender hover:bg-brand-lilac/50 transition-colors"
                                aria-label={`Editar ${p.name}`}
                                title="Editar"
                              >
                                <PencilSquareIcon className="h-5 w-5" aria-hidden />
                              </button>
                              <button
                                type="button"
                                onClick={() => askDeletePsych(p)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                aria-label={`Eliminar ${p.name}`}
                                title="Eliminar"
                              >
                                <TrashIcon className="h-5 w-5" aria-hidden />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {psychs.length > 0 && (
              <PaginationFooter
                page={psychSafePage}
                pageCount={psychPageCount}
                pageSize={psychPageSize}
                total={psychs.length}
                setPage={setPsychPage}
                setPageSize={setPsychPageSize}
                itemLabel="psicólogo"
              />
            )}
          </div>
          <AdminPsychDetailDrawer
            open={Boolean(selectedPsych)}
            psychologist={selectedPsych}
            onClose={() => setSelectedPsych(null)}
          />
        </section>
      )}

      {!loadingSection && !sectionError && activeSection === "appointments" && (
        <section className="scroll-mt-24 space-y-4">
          <div className="overflow-hidden rounded-xl border border-brand-lavender/20 bg-white shadow-sm">
            {appts.length === 0 ? (
              <div className="py-16 px-6 text-center">
                <p className="portal-value font-semibold text-brand-navy mb-1">
                  {hasActiveFilters || debouncedSearch
                    ? "Ninguna cita coincide con los filtros."
                    : "Sin citas en la ventana seleccionada."}
                </p>
                {!hasActiveFilters && !debouncedSearch ? (
                  <p className="portal-meta">
                    Ajusta el rango de fechas o activa "Ver historial completo".
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] table-fixed border-collapse">
                  <colgroup>
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-brand-lavender/15 bg-brand-lilac/20">
                      <th className="portal-table-head px-4 py-3 text-left">Fecha y hora</th>
                      <th className="portal-table-head px-4 py-3 text-left">Cliente</th>
                      <th className="portal-table-head px-4 py-3 text-left">Psicólogo</th>
                      <th className="portal-table-head px-4 py-3 text-left">Modalidad</th>
                      <th className="portal-table-head px-4 py-3 text-left">Estado</th>
                      <th className="portal-table-head px-4 py-3 text-right"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apptRows.map((a) => {
                      const { date, time } = formatApptTimeRange(a.startUtc, a.endUtc);
                      return (
                        <tr
                          key={a.appointmentId}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedAppt(a)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedAppt(a);
                            }
                          }}
                          className="border-b border-brand-lavender/10 last:border-0 hover:bg-brand-lilac/20 transition-colors cursor-pointer"
                        >
                          <td className="portal-table-cell px-4 py-3 align-middle">
                            <span className="block font-semibold text-brand-navy text-[0.9375rem] whitespace-nowrap">
                              {date}
                            </span>
                            <span className="block portal-meta whitespace-nowrap mt-0.5">
                              {time}
                            </span>
                          </td>
                          <td className="portal-table-cell px-4 py-3 align-middle overflow-hidden">
                            <span
                              className="block truncate font-semibold text-brand-navy"
                              title={a.clientName || undefined}
                            >
                              {displayCell(a.clientName)}
                            </span>
                          </td>
                          <td className="portal-table-cell px-4 py-3 align-middle overflow-hidden">
                            <span
                              className="block truncate text-brand-dark"
                              title={a.psychologistName || undefined}
                            >
                              {displayCell(a.psychologistName)}
                            </span>
                          </td>
                          <td className="portal-table-cell px-4 py-3 align-middle">
                            {a.modality ? (
                              <span className="inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold bg-blue-50 text-blue-700 border-blue-200">
                                {labelAppointmentModality(a.modality)}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="portal-table-cell px-4 py-3 align-middle">
                            <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold ${
                              a.status === "scheduled"
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : a.status === "cancelled"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-brand-lilac/40 text-brand-navy border-brand-lavender/30"
                            }`}>
                              {labelApptStatus(a.status)}
                            </span>
                          </td>
                          <td
                            className="portal-table-cell px-3 py-3 align-middle text-right"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {a.status === "scheduled" ? (
                              <button
                                type="button"
                                onClick={() => askCancelAppt(a)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                aria-label="Cancelar cita"
                                title="Cancelar cita"
                              >
                                <TrashIcon className="h-4 w-4" aria-hidden />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {appts.length > 0 && (
              <PaginationFooter
                page={apptSafePage}
                pageCount={apptPageCount}
                pageSize={apptPageSize}
                total={appts.length}
                setPage={setApptPage}
                setPageSize={setApptPageSize}
                itemLabel="cita"
              />
            )}
          </div>

          <AdminApptDetailDrawer
            open={Boolean(selectedAppt)}
            appt={selectedAppt}
            onClose={() => setSelectedAppt(null)}
            onCancel={askCancelAppt}
          />
        </section>
      )}

      <Dialog
        open={Boolean(deleteTarget)}
        handler={closeDeleteDialog}
        size="sm"
        className="!max-w-[calc(100vw-2rem)]"
      >
        <DialogHeader className="text-brand-dark">
          ¿Eliminar este psicólogo?
        </DialogHeader>
        <DialogBody className="text-brand-gray text-sm leading-relaxed space-y-2">
          <p>
            ¿Seguro que quieres eliminar este psicólogo? Esta acción no se puede
            deshacer.
          </p>
          {deleteTarget?.name && (
            <p className="text-brand-dark font-medium">{deleteTarget.name}</p>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="text"
            className="text-brand-gray normal-case"
            onClick={closeDeleteDialog}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white normal-case"
            onClick={confirmDeletePsych}
            disabled={deleting}
          >
            {deleting ? "Eliminando…" : "Confirmar"}
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={Boolean(cancelApptTarget)}
        handler={closeCancelApptDialog}
        size="sm"
        className="!max-w-[calc(100vw-2rem)]"
      >
        <DialogHeader className="text-brand-dark">¿Cancelar esta cita?</DialogHeader>
        <DialogBody className="text-brand-gray text-sm leading-relaxed space-y-2">
          <p>La cita quedará marcada como cancelada. Esta acción no elimina el registro.</p>
          {cancelApptTarget && (
            <p className="text-brand-dark font-medium">
              {cancelApptTarget.clientName || "Cliente"} —{" "}
              {formatApptTimeRange(cancelApptTarget.startUtc, cancelApptTarget.endUtc).date}
            </p>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="text"
            className="text-brand-gray normal-case"
            onClick={closeCancelApptDialog}
            disabled={cancellingAppt}
          >
            Volver
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white normal-case"
            onClick={confirmCancelAppt}
            disabled={cancellingAppt}
          >
            {cancellingAppt ? "Cancelando…" : "Confirmar cancelación"}
          </Button>
        </DialogFooter>
      </Dialog>
    </PortalShell>
  );
}
